import {
    BillByCounterpartySortValue,
    BillSortValue,
    BillViewOptionGroup,
} from "@common/types/bill";
import { BillFilters } from "@common/contracts/bill.contract";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useBillsPageSortingCache } from "@hooks/view/useBillsPageSortingCache";
import { useBillsByCounterpartyPageSortingCache } from "@hooks/view/useBillsByCounterpartyPageSortingCache";
import {
    getBillExportFilename,
    getWorkspaceExportDate,
} from "@common/helpers/spreadsheet/workspaceListExportFilename";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { getBillsXlsx } from "@lib/bills";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { DownloadIcon } from "@icons";
import { toBillApiFilters } from "../../contexts/BillsViewContext";
import { getSortExpression as getBillSortExpression } from "./useSort";
import { getSortExpression as getVendorSortExpression } from "./useSortByCounterparty";

interface Props {
    filters: BillFilters | undefined;
    groupBy: BillViewOptionGroup;
    columns: string[];
}

function getExportSort({
    groupBy,
    billSort,
    vendorSort,
}: {
    groupBy: BillViewOptionGroup;
    billSort: BillSortValue | undefined;
    vendorSort: BillByCounterpartySortValue | undefined;
}) {
    if (groupBy === BillViewOptionGroup.COUNTERPARTY) {
        return getVendorSortExpression(
            vendorSort ?? BillByCounterpartySortValue.OPEN_DESC,
        );
    }

    return getBillSortExpression(billSort ?? BillSortValue.DATE_DESC);
}

export const BillsExport: React.FC<Props> = ({ filters, groupBy, columns }) => {
    const activeWorkspace = useActiveWorkspace();
    const [cachedBillSort] = useBillsPageSortingCache();
    const [cachedVendorSort] = useBillsByCounterpartyPageSortingCache();
    const { download, isDownloading } = useFileDownload(async () => {
        if (!activeWorkspace) {
            return;
        }

        const response = await getBillsXlsx({
            workspaceId: activeWorkspace.id,
            filters: toBillApiFilters(filters),
            groupBy,
            columns,
            sort: getExportSort({
                groupBy,
                billSort: cachedBillSort,
                vendorSort: cachedVendorSort,
            }),
        });
        saveBlobResponse(
            response,
            getBillExportFilename({
                workspaceName: activeWorkspace.name,
                groupBy,
                date: getWorkspaceExportDate({
                    timezone: activeWorkspace.timezone,
                }),
            }),
        );
    });

    return (
        <ButtonWithLoader
            variant="primary"
            iconOnly
            size="md"
            aria-label="Download bills as Excel"
            loading={isDownloading}
            onClick={() => {
                void download();
            }}
        >
            <DownloadIcon />
        </ButtonWithLoader>
    );
};
